import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { APP_TITLE } from '@/config/app'
import { http } from '@/lib/http'
import type { LegalSiteConfigClientVo } from '@/types/api'

export const SITE_CONFIG_QUERY_KEY = ['site', 'config'] as const
export const DEFAULT_USER_AGREEMENT_HREF = '/policy/user-agreement'
export const DEFAULT_PRIVACY_POLICY_HREF = '/policy/privacy'
export const DEFAULT_FAVICON_HREF = '/logo.svg'

export function fetchSiteConfig() {
  return http.get<LegalSiteConfigClientVo>('/client/legal/site/config')
}

export function useSiteConfig() {
  return useQuery({
    queryKey: SITE_CONFIG_QUERY_KEY,
    queryFn: fetchSiteConfig,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  })
}

export function resolveSiteTitle(site?: Pick<LegalSiteConfigClientVo, 'siteName'> | null): string {
  return normalizeSiteText(site?.siteName) || APP_TITLE
}

export function resolveSiteLogoUrl(
  site?: Pick<LegalSiteConfigClientVo, 'siteLogoUrl'> | null,
): string {
  return normalizeSiteText(site?.siteLogoUrl)
}

/** 左侧菜单副标题 (例如 "智能法律顾问"), 为空时由调用方回退到写死文案 */
export function resolveSiteBrief(site?: Pick<LegalSiteConfigClientVo, 'siteBrief'> | null): string {
  return normalizeSiteText(site?.siteBrief)
}

/** 左侧菜单图标, 为空时回退到站点 Logo */
export function resolveMenuLogoUrl(
  site?: Pick<LegalSiteConfigClientVo, 'menuLogoUrl' | 'siteLogoUrl'> | null,
): string {
  return normalizeSiteText(site?.menuLogoUrl) || resolveSiteLogoUrl(site)
}

export function useSiteTitle(): string {
  const siteQuery = useSiteConfig()
  return resolveSiteTitle(siteQuery.data)
}

export function useSiteLogoUrl(): string {
  const siteQuery = useSiteConfig()
  return resolveSiteLogoUrl(siteQuery.data)
}

export function useSiteBrief(): string {
  const siteQuery = useSiteConfig()
  return resolveSiteBrief(siteQuery.data)
}

export function useMenuLogoUrl(): string {
  const siteQuery = useSiteConfig()
  return resolveMenuLogoUrl(siteQuery.data)
}

/** 客服 / 服务号二维码地址, 为空表示后台尚未配置 */
export function resolveSiteQrCodeUrl(
  site?: Pick<LegalSiteConfigClientVo, 'wechatMpQrCodeUrl'> | null,
): string {
  return normalizeSiteText(site?.wechatMpQrCodeUrl)
}

export function SiteTitleBootstrap() {
  const siteTitle = useSiteTitle()

  useEffect(() => {
    document.title = siteTitle
  }, [siteTitle])

  return null
}

export function SiteFaviconBootstrap() {
  // 浏览器标签图标与左侧菜单图标保持一致 (menuLogoUrl 为空时回退到站点 Logo)
  const menuLogoUrl = useMenuLogoUrl()

  useEffect(() => {
    const href = menuLogoUrl || DEFAULT_FAVICON_HREF
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    // 站点接口配置的图标多为位图，移除写死的 svg type，让浏览器按实际类型解析
    if (menuLogoUrl) {
      link.removeAttribute('type')
    }
    link.href = href
  }, [menuLogoUrl])

  return null
}

export function resolveSitePolicyLinks(
  site?: Pick<LegalSiteConfigClientVo, 'userAgreementUrl' | 'privacyPolicyUrl'> | null,
  from?: string,
) {
  return {
    userAgreementHref:
      normalizeSiteText(site?.userAgreementUrl) ||
      appendFromQuery(DEFAULT_USER_AGREEMENT_HREF, from),
    privacyPolicyHref:
      normalizeSiteText(site?.privacyPolicyUrl) ||
      appendFromQuery(DEFAULT_PRIVACY_POLICY_HREF, from),
  }
}

export function useSitePolicyLinks(from?: string) {
  const siteQuery = useSiteConfig()
  return resolveSitePolicyLinks(siteQuery.data, from)
}

export function isExternalSiteHref(href: string): boolean {
  return /^https?:\/\//i.test(href)
}

function normalizeSiteText(value: string | undefined | null): string {
  return value?.trim() ?? ''
}

function appendFromQuery(href: string, from: string | undefined): string {
  const normalizedFrom = normalizeSiteText(from)
  if (!normalizedFrom) return href
  const separator = href.includes('?') ? '&' : '?'
  return `${href}${separator}from=${encodeURIComponent(normalizedFrom)}`
}
